// src/components/common/ConfirmMorphActions.js
//
// The two-step confirm footer. Three states in a row that re-lays-out ONCE:
//
//   rest       [ Cancel ][ Delete employee ]
//   armed      [ Yes, delete Carla Núñez ................ ]   ✕ in the corner
//   committing (identical geometry — only the label and colour change)
//
// Motion is the LogoutConfirmDialog vocabulary, extended from its busy state
// back to arming: 320ms, cubic-bezier(.16,1,.3,1), Cancel collapsing while the
// primary becomes a capsule. Nothing new was invented.
//
// ── The measurement, and why a class swap is not enough ──────────────────────
// `overlayPrimaryAction` is `flex: 1 1 auto` on purpose, so a long Spanish
// label gets the width it needs. But `flex-basis: auto` CANNOT interpolate with
// a length, so animating to a fixed width by toggling a class makes Cancel snap
// instead of glide — in both directions. The resting width is measured, written
// inline, committed as a start frame, then tweened; the stylesheet gets the
// width back when the tween lands so content-sizing returns for long labels.
//
// The corner ✕ CLOSES — it does not step back to rest. ✕ means dismiss
// everywhere else in this app (twelve modals) and it never means "back one
// step". It is the same control as Cancel in a different shape, so the escape
// hatch changes position and form across the morph but never changes meaning.
// Its accessible name is `common.cancel`, describing the function.
//
// ── FILL inside the capsule (WAVE 1 · ENGINE, 2026-09-24) ────────────────────
// A two-tap DELETE plays its action beat INSIDE the armed capsule rather than
// swapping in a second element (the morph capsule is ONE control that absorbs
// every state — see the PLAYBOOK's "Confirm-modal action beat" entry). The
// optional `beat` prop ({ phase, tone, workingLabel, successLabel }) is that:
// once the SECOND tap commits and `phase` moves off 'idle', the SAME button —
// already the full-width capsule the first tap produced — starts filling with
// the identical sweep→crawl visual ActionBeatFill uses (FillButtonContent),
// then lands on a finish colour + drawn ✓ on success. Nothing about the
// capsule's SHAPE changes again after arming; only its interior content and
// background do. Absent `beat` (every caller before this landed, and every
// morph gate that isn't also a `kind:'fill'` action beat), this component is
// byte-identical to before — the whole block below is gated on `beat` being
// truthy.
//
// ── `equalWidth` (opt-in, worker-offboarding R2, 2026-09-24) ─────────────────
// The REST-state row historically sizes both buttons off `flex: '1 1 auto'`
// (content-basis growth — `overlayPrimaryAction`'s own doc comment: "lets a
// long label get the width it needs"), which is correct for most callers but
// reads as lopsided/left-leaning for the Team Access modal family, whose
// locked spec ("two EQUAL buttons that fill the row") wants a strict 50/50
// split regardless of label length. `equalWidth` overrides ONLY the resting
// `flex` shorthand to `'1 1 0%'` (basis 0 — both buttons grow equally from
// nothing, landing on equal pixel widths) — it does NOT touch the armed-state
// morph, which manipulates `flexGrow`/`flexBasis` imperatively via direct DOM
// writes (`applyWidth` below) and works identically regardless of the resting
// basis. Absent (every existing caller), `equalWidthFlex` is `null` and
// spreading `...null` is a no-op, so this component stays byte-identical.
//
// ── `checking` (opt-in, worker-offboarding R2 follow-up, 2026-09-24) ────────
// A pre-commit async check (e.g. "does this person have work history?")
// opens the REST-state gate in its FINAL shape immediately — same radius,
// same size, same label — rather than a separate "checking…" affordance
// that then gets replaced (founder feedback: the old swap read as a
// squared-off placeholder button). `checking` disables the confirm button
// (opacity ~0.55, `aria-busy`, taps do nothing — Cancel/Esc/scrim stay live,
// same contract as `ConfirmDialogActions`' `checking` branch) and plays a
// soft opacity pulse on the LABEL only (1.2s ease-in-out loop; reduced
// motion holds it static) via a lazily-injected keyframe, same pattern as
// `skeletonMotion.js`. The moment the check resolves, this same prop simply
// goes false — no remount, no shape change, just the disabled/pulse styling
// lifting.

import { useCallback, useEffect, useRef, useState } from 'react';
import { foundation } from '../../ui/tokens';
import { typedConfirmActionsRow } from '../../ui/confirmDialogStyles';
import { FILL_MS } from '../../ui/actionBeatMotion';
import FillButtonContent, { finishStyle } from './actionBeat/FillButtonContent';
import ConfirmMorphCloseButton from './ConfirmMorphCloseButton';
import useOverlayFooter from './overlay/useOverlayFooter';

const { spacePx, radii } = foundation;

const MORPH_MS = 320;
const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';
export const CAPSULE_RADIUS = 24;

let checkingPulseInjected = false;
export function injectCheckingPulseKeyframes() {
  if (checkingPulseInjected || typeof document === 'undefined') return;
  checkingPulseInjected = true;
  const s = document.createElement('style');
  s.setAttribute('data-cvx-checking-pulse', 'true');
  s.textContent = `
    @keyframes cvx-checking-pulse-label { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
    .cvx-checking-pulse-label { display: inline-block; animation: cvx-checking-pulse-label 1.2s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) {
      .cvx-checking-pulse-label { animation: none; opacity: 0.75; }
    }
  `;
  document.head.appendChild(s);
}

const morphTransition = [
  `flex-basis ${MORPH_MS}ms ${EASE}`,
  `flex-grow ${MORPH_MS}ms ${EASE}`,
  `border-radius ${MORPH_MS}ms ${EASE}`,
  `padding ${MORPH_MS}ms ${EASE}`,
  `margin ${MORPH_MS}ms ${EASE}`,
  `opacity ${MORPH_MS}ms ${EASE}`,
  `border-width ${MORPH_MS}ms ${EASE}`,
].join(', ');

export default function ConfirmMorphActions({
  armed: armedProp,
  onActivate,
  onCancel,
  ctaLabel,
  step2Label,
  cancelLabel,
  tone,
  disabled = false,
  busy = false,
  hasTypedField = false,
  reduced = false,
  /**
   * Button skins. ConfirmDialogInline renders admin-themed buttons off its
   * `styles` object rather than overlayStyles, so it injects its own — the
   * MOTION and the state machine stay identical either way, which is the
   * whole point of sharing this component instead of copying it.
   */
  confirmStyleBase,
  cancelStyleBase,
  /**
   * Opt-in — see the `equalWidth` note above. When true, the RESTING Cancel
   * and confirm buttons split the row exactly 50/50 instead of sizing to
   * their own label content. Never affects the armed capsule (already
   * full-width) or any existing caller (defaults to false).
   */
  equalWidth = false,
  /**
   * Opt-in — see the `checking` note above. Disables ONLY the confirm
   * button (Cancel/✕ stay live) in its FINAL rest shape, with a soft label
   * pulse. Never arms, never calls `onActivate`. Absent (every existing
   * caller), behavior is byte-identical.
   */
  checking = false,
  /**
   * Optional — see the FILL-inside-the-capsule note above. `phase` is the
   * SAME 'idle'|'working'|'success' the engine's other action-beat visuals
   * use (straight off `useModalActionBeat`); `tone` picks the finish colour
   * ('delete'/'security' → neutral, 'save' → semantic success);
   * `workingLabel`/`successLabel` are the caller's own beat copy.
   */
  beat,
  /**
   * Opt-in — when true, this component renders NO ✕ of its own; the shell
   * (ConfirmDialog / ConfirmDialogInline) renders `ConfirmMorphCloseButton`
   * as a sibling instead, so the ✕ anchors to the PANEL. Every other caller
   * omits it and stays byte-identical.
   */
  hideCloseButton = false,
  /** Opt-in test id for the confirm button (the bulk delete pop-up keeps its own); default unchanged. */
  confirmTestId = 'confirm-dialog-confirm-button',
}) {
  const cancelRef = useRef(null);
  const restWidthRef = useRef(0);

  // The ONE footer geometry (KAN-948): 38px web / 44px touch, Cancel outlined. A caller (the admin
  // dialog) may still inject its own skins; the MOTION is identical either way.
  const footer = useOverlayFooter();
  const confirmBase = confirmStyleBase
    || (tone === 'danger' ? footer.danger : footer.primary);
  const cancelBase = cancelStyleBase || footer.cancel;
  const equalWidthFlex = equalWidth ? { flex: '1 1 0%' } : null;

  const beatPhase = beat?.phase || 'idle';
  // Active the instant the beat leaves idle — matches ActionBeatFill's
  // `activeBeat` naming (working OR success, both disable further taps).
  const beatActive = Boolean(beat) && beatPhase !== 'idle';
  // Effective busy: the beat's own working/success phases lock the capsule
  // exactly like the legacy `busy` prop always has, so every existing
  // busy-gated branch below (✕, Cancel text, Enter) covers this for free.
  const effectiveBusy = busy || beatActive;
  // Keep the ARMED capsule geometry for the whole beat (security review,
  // 2026-09-24): the gate disarms the moment the second tap commits, and
  // without this the capsule sprang back to its resting two-button shape
  // mid-fill. Every `armed` read below is the visual/structural state.
  const armed = armedProp || beatActive;
  // Never arms, never calls onActivate — the checking window is strictly
  // before the first tap can mean anything.
  const confirmDisabled = effectiveBusy || disabled || checking;

  useEffect(() => {
    if (checking && !reduced) injectCheckingPulseKeyframes();
  }, [checking, reduced]);

  // Sweep → crawl staging — identical schedule to ActionBeatFill, just keyed
  // off the beat's phase instead of a locally-owned mount trick: this
  // component is continuously mounted from rest onward, so 'working' always
  // arrives as a live prop change (never a mount-time jump), and a plain
  // phase-driven effect is enough to animate it in.
  const [stage, setStage] = useState('start');
  useEffect(() => {
    if (reduced) { setStage('run'); return undefined; }
    if (beatPhase !== 'working') return undefined;
    setStage('run');
    const t = setTimeout(() => setStage('crawl'), FILL_MS.crawlAfter);
    return () => clearTimeout(t);
  }, [beatPhase, reduced]);

  // Measure BEFORE the first arm, while the button is still at its resting
  // width. Re-measured on every return to rest so a locale or container change
  // can't leave a stale number behind.
  useEffect(() => {
    const node = cancelRef.current;
    if (!node || reduced) return;
    if (!armed) {
      // Let the tween back to rest finish before trusting a new measurement.
      const t = setTimeout(() => {
        if (cancelRef.current && !armed) {
          // `equalWidth` rests on the React-inline `flex: 1 1 0%`; clearing the
          // longhands would delete it (Cancel shrank to its content width), so
          // restore them instead of blanking.
          cancelRef.current.style.flexGrow = equalWidth ? '1' : '';
          cancelRef.current.style.flexBasis = equalWidth ? '0%' : '';
          restWidthRef.current = cancelRef.current.offsetWidth;
        }
      }, MORPH_MS + 20);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [armed, reduced, equalWidth]);

  const applyWidth = useCallback((next) => {
    const node = cancelRef.current;
    if (!node) return;
    if (reduced) {
      node.style.flexGrow = next === 'rest' ? (equalWidth ? '1' : '') : '0';
      node.style.flexBasis = next === 'rest' ? (equalWidth ? '0%' : '') : '0px';
      return;
    }
    if (next === 'armed') {
      const from = node.offsetWidth || restWidthRef.current;
      restWidthRef.current = from || restWidthRef.current;
      node.style.flexGrow = '0';
      node.style.flexBasis = `${from}px`;
      void node.offsetWidth;            // commit the start frame
      node.style.flexBasis = '0px';     // …then tween to nothing
    } else {
      node.style.flexBasis = `${restWidthRef.current || node.offsetWidth}px`;
      node.style.flexGrow = '1';
    }
  }, [reduced, equalWidth]);

  const armedRef = useRef(armed);
  useEffect(() => {
    if (armedRef.current !== armed) {
      armedRef.current = armed;
      applyWidth(armed ? 'armed' : 'rest');
    }
  }, [armed, applyWidth]);

  const cancelStyle = {
    ...cancelBase,
    ...equalWidthFlex,
    transition: reduced ? 'none' : morphTransition,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    ...(armed ? {
      paddingLeft: 0,
      paddingRight: 0,
      marginRight: 0,
      opacity: 0,
      borderWidth: 0,
      minWidth: 0,
      pointerEvents: 'none',
    } : null),
  };

  const confirmStyle = {
    ...confirmBase,
    ...equalWidthFlex,
    transition: reduced ? 'none' : morphTransition,
    borderRadius: armed ? CAPSULE_RADIUS : radii.md,
    ...(disabled ? { opacity: 0.45, cursor: 'not-allowed' } : null),
    // The legacy busy fade only applies while the beat ISN'T driving the
    // button — once it is, the fill overlay + finish colour own the look.
    ...(busy && !beatActive ? { opacity: 0.7, cursor: 'not-allowed' } : null),
    ...(beat ? { position: 'relative', overflow: 'hidden' } : null),
    ...(beatActive ? { cursor: beatPhase === 'success' ? 'default' : 'not-allowed' } : null),
    ...(beatPhase === 'success' ? finishStyle(beat.tone) : null),
    // Checking wins last — same FINAL shape/radius/size as the resolved
    // state, just a static reduced opacity on the button (the pulse lives on
    // the label span below, not the whole button).
    ...(checking ? { opacity: 0.55, cursor: 'not-allowed' } : null),
  };

  return (
    <>
      {!hideCloseButton && (
        // Visible + reachable only once armed. (The aria-hidden / tabIndex pair used to be exactly
        // BACKWARDS: the only visible escape of an armed destructive gate was keyboard-unreachable,
        // and at rest an INVISIBLE, pointer-inert ✕ stayed focusable - `pointer-events: none` does
        // not block keyboard activation. ConfirmMorphCloseButton carries the corrected contract.)
        <ConfirmMorphCloseButton
          armed={armed}
          busy={effectiveBusy}
          onCancel={onCancel}
          reduced={reduced}
        />
      )}

      {/*
        Arming is otherwise SILENT to a screen reader: the tone shift and the
        ✕ appearing are visual-only, and the focused button's accessible name
        changing from "Return to Sent" to "Sí, retirar la aprobación" is
        announced by most readers but guaranteed by none. One visually-hidden
        live region says it once, at the moment the second step opens.
      */}
      <span
        role="status"
        aria-live="polite"
        data-testid="confirm-morph-armed-status"
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          margin: -1,
          padding: 0,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
          clipPath: 'inset(50%)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      >
        {armed ? (step2Label || ctaLabel) : ''}
      </span>

      <div style={{ ...typedConfirmActionsRow({ afterInput: hasTypedField }), gap: 0 }}>
        {cancelLabel ? (
          <button
            ref={cancelRef}
            type="button"
            style={{ ...cancelStyle, marginRight: armed ? 0 : spacePx[3] }}
            onClick={effectiveBusy ? undefined : onCancel}
            disabled={effectiveBusy || armed}
            aria-hidden={armed}
            tabIndex={armed ? -1 : 0}
            data-testid="confirm-dialog-cancel-button"
          >
            {cancelLabel}
          </button>
        ) : null}
        <button
          type="button"
          style={confirmStyle}
          onClick={confirmDisabled ? undefined : onActivate}
          disabled={confirmDisabled}
          aria-busy={checking ? 'true' : undefined}
          data-testid={confirmTestId}
          data-armed={armed ? 'true' : 'false'}
        >
          {beatActive ? (
            <FillButtonContent
              phase={beatPhase}
              stage={stage}
              reduced={reduced}
              confirmLabel={step2Label || ctaLabel}
              workingLabel={beat.workingLabel}
              successLabel={beat.successLabel}
            />
          ) : checking ? (
            <span className={reduced ? undefined : 'cvx-checking-pulse-label'}>{ctaLabel}</span>
          ) : (
            armed ? (step2Label || ctaLabel) : ctaLabel
          )}
        </button>
      </div>
    </>
  );
}
